<template>
  <ModuleBuilder
    title="Build a New Survey"
    :steps="steps"
    @close="handleClose"
    :on-next="handleNext"
    :loading="loadingLearningModule"
    :last-step-label="learningModule.status === 'Draft' ? 'Save' : 'Save & Publish'"
  >
    <template #step-content="{ currentStepKey, currentSubstepKey }">
      <InitializeSurveyStep
        ref="initializeSurveyStep"
        v-if="currentStepKey === 'survey-details'"
        :clientId="learningModule.clientId"
        :name="learningModule.name"
        :description="learningModule.description"
        :bannerFile="file"
        :existingImageUrl="learningModule.imageUrl"
        :defaultImageURL="defaultImageURL"
        :isEditing="isEditing || !!learningModule.learningModuleId"
        @update:clientId="updateLearningModule({ clientId: $event })"
        @update:name="updateLearningModule({ name: $event, displayName: $event })"
        @update:description="updateLearningModule({ description: $event })"
        @update:bannerFile="updateFile"
        @clear-existing-image="updateLearningModule({ imageUrl: null })"
      />
      <GroupsStep
        v-else-if="currentStepKey === 'group'"
        :visibilityType="learningModule.visibilityType"
        :selectedGroups="selectedGroups"
        :isEditing="isEditing"
        @update:visibilityType="updateLearningModule({ visibilityType: $event })"
        @update:selectedGroups="selectedGroups = $event"
      />
      <IntroductionMessageStep
        ref="introductionStep"
        v-else-if="currentStepKey === 'surveySections' && currentSubstepKey === 'introduction'"
        :enabled="introductionEnabled"
        :displayName="introductionCard.displayName"
        :content="introductionCard.content"
        :clientId="globalClientId"
        @update:enabled="introductionEnabled = $event"
        @update:displayName="updateIntroductionCard({ displayName: $event })"
        @update:content="updateIntroductionCard({ content: $event })"
      />
      <SurveyQuestionStep
        ref="questionStep"
        v-else-if="currentStepKey === 'surveySections' && currentSubstepKey === 'questions'"
        :questions="questions"
        :repeatable="repeatable"
        @update:questions="questions = $event"
        @update:repeatable="repeatable = $event"
        @deleted-questions="deletedQuestions.push(...$event)"
        @deleted-answers="deletedAnswers.push(...$event)"
      />
      <CompletionMessageStep
        ref="completionStep"
        v-else-if="currentStepKey === 'surveySections' && currentSubstepKey === 'completion'"
        :displayName="completionCard.displayName"
        :content="completionCard.content"
        :clientId="globalClientId"
        @update:displayName="updateCompletionCard({ displayName: $event })"
        @update:content="updateCompletionCard({ content: $event })"
      />
      <ReviewSurveyStep
        v-else-if="currentStepKey === 'confirmAndLaunch'"
        :visibility="learningModule.visibilityType"
        :groups="selectedGroups"
        :repeatable="repeatable"
        :introductionEnabled="introductionEnabled"
        :questionCount="questions.length"
        :isDraft="learningModule.status === 'Draft'"
        :learningModuleId="learningModule.learningModuleId"
        @update:isDraft="updateLearningModule({ status: $event ? 'Draft' : 'Active' })"
      />
    </template>
    <template #success-content>
      <SuccessStep
        type="survey"
        :action="learningModule.status === 'Draft' ? 'saved' : 'published'"
        @close="handleClose"
      />
    </template>
  </ModuleBuilder>
</template>

<script>
import ModuleBuilder from "@/components/module-builder/ModuleBuilder.vue";
import InitializeSurveyStep from "./InitializeSurveyStep.vue";
import CompletionMessageStep from "./CompletionMessageStep.vue";
import IntroductionMessageStep from "./IntroductionMessageStep.vue";
import SurveyQuestionStep from "./SurveyQuestionStep.vue";
import ReviewSurveyStep from "./ReviewSurveyStep.vue";
import GroupsStep from "./GroupsStep.vue";
import SuccessStep from "@/components/module-builder/components/SuccessStep.vue";
import { mapState } from "vuex";
import LearningService from "@/services/LearningService";
import GroupService from "@/services/GroupService";
import { uploadEmbeddedContent, loadEmbeddedContent } from "@/utils/embeddedContentHelper";
import hostConfig from "@/config/hostResolver";

const STEP_ORDER = ["survey-details", "group", "surveySections", "confirmAndLaunch"];

const LEARNING_MODULE_INITIAL_STATE = {
  learningModuleId: null,
  clientId: null,
  name: "",
  displayName: "",
  description: "",
  type: "Survey",
  visibilityType: "PRIVATE",
  status: "Draft",
  forceLinearProgress: false,
  imageUrl: null,
  LearningChapters: [],
};

const LEARNING_CARD_INITIAL_STATE = {
  learningCardId: null,
  learningChapterId: null,
  clientId: null,
  displayName: "",
  content: "",
  type: "text",
};

export default {
  name: "SurveyBuilder",
  components: {
    ModuleBuilder,
    InitializeSurveyStep,
    CompletionMessageStep,
    IntroductionMessageStep,
    SurveyQuestionStep,
    ReviewSurveyStep,
    GroupsStep,
    SuccessStep,
  },
  data() {
    return {
      file: null,
      learningModule: { ...LEARNING_MODULE_INITIAL_STATE },
      defaultImageURL: `${hostConfig.apiBaseUrl}/public-assets/Surveys/default-image.png`,
      loadingLearningModule: false,

      // Survey chapter/card (holds quiz questions)
      surveyChapterId: null,
      surveyCardId: null,
      repeatable: false,
      questions: [],
      deletedQuestions: [],
      deletedAnswers: [],

      // Introduction chapter/card
      introductionEnabled: false,
      introductionCard: { ...LEARNING_CARD_INITIAL_STATE },

      // Completion chapter/card
      completionCard: { ...LEARNING_CARD_INITIAL_STATE },

      // Group
      selectedGroups: [],
    };
  },

  computed: {
    ...mapState(["globalClientId"]),

    steps() {
      const surveyLabel = this.learningModule.name || "Survey";
      const questionCount = this.questions.length;
      const introLabel = this.introductionEnabled
        ? "Introduction (optional): Added"
        : "Introduction (optional)";
      const questionsLabel = questionCount === 1 ? "1 question" : `${questionCount} questions`;
      const groupLabel =
        this.selectedGroups.length === 1
          ? `Group: ${this.selectedGroups[0].groupDisplayName}`
          : this.selectedGroups.length > 1
          ? `Group: ${this.selectedGroups.length} selected`
          : "Group";

      const stepDefs = {
        "survey-details": {
          key: "survey-details",
          label: surveyLabel,
          icon: "mdi-comment-question",
        },
        group: {
          key: "group",
          label: groupLabel,
          icon: "mdi-account-multiple",
        },
        surveySections: {
          key: "surveySections",
          label: "Survey sections",
          icon: "mdi-alpha-s",
          substeps: [
            { key: "introduction", label: introLabel, icon: "mdi-text-box" },
            { key: "questions", label: questionsLabel, icon: "mdi-comment-question" },
            { key: "completion", label: "Completion message", icon: "mdi-text-box" },
          ],
        },
        confirmAndLaunch: {
          key: "confirmAndLaunch",
          label: "Confirm and launch",
          icon: "mdi-rocket-launch",
        },
      };

      return STEP_ORDER.map((key) => stepDefs[key]).filter(Boolean);
    },
    isEditing() {
      return (
        this.$route.query.clientId !== undefined &&
        this.$route.query.clientId !== null &&
        this.$route.query.learningModuleId !== undefined &&
        this.$route.query.learningModuleId !== null
      );
    },
  },
  methods: {
    handleClose() {
      this.$router.push({ name: "programadmin", query: { tab: "surveys" } });
    },
    async handleNext(stepKey, substepKey) {
      if (stepKey === "survey-details" || stepKey === "confirmAndLaunch") {
        // This ref won't exist if we're on confirmAndLaunch step
        if (this.$refs.initializeSurveyStep) {
          const valid = this.$refs.initializeSurveyStep?.validate();
          if (!valid) return false;
        }
        try {
          if (!this.learningModule.learningModuleId) {
            await this.createSurvey();
          } else {
            await this.updateSurveyDetails();
          }
        } catch {
          this.$store.dispatch("showError", { message: "Error creating survey" });
          return false;
        }
      } else if (stepKey === "group") {
        if (!this.selectedGroups.length) {
          this.$store.dispatch("showError", { message: "Please select at least one group" });
          return false;
        }
        try {
          await this.saveLearningModule();
        } catch {
          this.$store.dispatch("showError", { message: "Error saving groups" });
          return false;
        }
      } else if (stepKey === "surveySections") {
        if (substepKey === "introduction") {
          if (!this.$refs.introductionStep?.validate()) return false;
          try {
            await this.saveIntroduction();
          } catch {
            this.$store.dispatch("showError", { message: "Error saving introduction" });
            return false;
          }
        } else if (substepKey === "questions") {
          if (!this.questions.length) {
            this.$store.dispatch("showError", { message: "Please add at least one question" });
            return false;
          }

          if (!this.$refs.questionStep?.validate()) return false;
          try {
            await this.saveQuestions();
          } catch {
            this.$store.dispatch("showError", { message: "Error saving questions" });
            return false;
          }
        } else if (substepKey === "completion") {
          if (!this.$refs.completionStep?.validate()) return false;
          try {
            await this.saveCompletion();
          } catch {
            this.$store.dispatch("showError", { message: "Error saving completion" });
            return false;
          }
        }
      }
    },
    updateLearningModule(values) {
      this.learningModule = { ...this.learningModule, ...values };
    },
    updateIntroductionCard(values) {
      this.introductionCard = { ...this.introductionCard, ...values };
    },
    updateCompletionCard(values) {
      this.completionCard = { ...this.completionCard, ...values };
    },
    updateFile(file) {
      this.file = file;
    },
    async uploadFile() {
      try {
        const response = await LearningService.replaceLearningModuleImage(
          this.learningModule.learningModuleId,
          this.globalClientId,
          this.file
        );
        this.learningModule.imageUrl = response.imageUrl;
      } catch {
        this.$store.dispatch("showError", { message: "Error uploading image" });
      }
    },
    async createSurvey() {
      const response = await LearningService.createLearningModule(this.learningModule);

      this.learningModule = response.module;

      this.loadCompletionFromModule();

      const chapters = this.learningModule.LearningChapters || [];
      const surveyChapter = chapters.find((ch) => !ch.introduction && !ch.conclusion);
      if (surveyChapter) {
        this.surveyChapterId = surveyChapter.learningChapterId;
        if (surveyChapter.LearningCards?.length) {
          this.surveyCardId = surveyChapter.LearningCards[0].learningCardId;
        }
      }

      // Process the file last - this way if it fails, we already created and stored the survey, including the ID in case they retry
      if (this.file) await this.uploadFile();
    },
    async saveLearningModule() {
      await LearningService.updateLearningModule(this.learningModule.learningModuleId, {
        ...this.learningModule,
        LearningModuleGroups: this.selectedGroups.map((g) => g.groupId),
      });
    },
    async updateSurveyDetails() {
      await this.saveLearningModule();
      if (this.file) await this.uploadFile();
    },
    async saveIntroduction() {
      if (this.introductionEnabled) {
        const embedded = await uploadEmbeddedContent(
          this.introductionCard.content,
          this.globalClientId
        );
        this.introductionCard.content = embedded.local;
        const dbContent = embedded.content;

        if (!this.introductionCard.learningChapterId) {
          const chapter = await LearningService.createLearningChapter({
            clientId: this.globalClientId,
            learningModuleId: this.learningModule.learningModuleId,
            introduction: true,
            name: "Introduction",
            displayName: "Introduction",
            order: 0,
          });
          this.introductionCard.learningChapterId = chapter.learningChapterId;

          const cardResult = await LearningService.createLearningCardInChapter(
            this.introductionCard.learningChapterId,
            {
              clientId: this.globalClientId,
              displayName: this.introductionCard.displayName,
              name: this.introductionCard.displayName,
              content: dbContent,
              type: this.introductionCard.type,
              order: 0,
            }
          );
          const card = cardResult?.card;
          if (!card) throw new Error("No card created");
          this.introductionCard.learningCardId = card.learningCardId;
        } else {
          await LearningService.updateLearningCard(this.introductionCard.learningCardId, {
            clientId: this.globalClientId,
            displayName: this.introductionCard.displayName,
            name: this.introductionCard.displayName,
            content: dbContent,
            type: this.introductionCard.type,
          });
        }
      } else if (this.introductionCard.learningChapterId) {
        await LearningService.deleteLearningChapter(this.introductionCard.learningChapterId);
        this.introductionCard = { ...LEARNING_CARD_INITIAL_STATE };
      }
    },
    async saveQuestions() {
      if (!this.surveyCardId) throw new Error("Survey card not found");

      await LearningService.updateLearningCard(this.surveyCardId, {
        repeatable: this.repeatable,
        clientId: this.globalClientId,
      });

      // Stamp clientId and order onto each question/answer before posting
      this.questions.forEach((question, qIndex) => {
        question.clientId = this.globalClientId;
        question.order = qIndex;
        (question.LearningQuizAnswers || []).forEach((answer, aIndex) => {
          answer.order = aIndex;
        });
      });

      const saved = await LearningService.postLearningQuiz(
        this.surveyCardId,
        this.questions,
        false
      );
      if (Array.isArray(saved)) {
        this.questions = saved;
      }

      // Delete removed questions
      if (this.deletedQuestions.length) {
        await LearningService.deleteLearningQuizQuestion(this.deletedQuestions);
        this.deletedQuestions = [];
      }

      // Delete removed answers (soft-delete via status patch)
      for (const answerId of this.deletedAnswers) {
        await LearningService.deleteLearningQuizAnswer(answerId);
      }
      this.deletedAnswers = [];
    },
    async saveCompletion() {
      if (!this.completionCard.learningCardId) throw new Error("Completion card not found");

      const embedded = await uploadEmbeddedContent(
        this.completionCard.content,
        this.globalClientId
      );
      this.completionCard.content = embedded.local;

      await LearningService.updateLearningCard(this.completionCard.learningCardId, {
        clientId: this.globalClientId,
        displayName: this.completionCard.displayName,
        name: this.completionCard.displayName,
        content: embedded.content,
        type: this.completionCard.type,
      });
    },

    async loadLearningModule() {
      const { learningModuleId, clientId } = this.$route.query;
      if (!this.isEditing) return;

      this.loadingLearningModule = true;
      try {
        const response = await LearningService.getModulesAdmin(
          "survey",
          clientId,
          false,
          false,
          1,
          learningModuleId
        );
        if (response.modules?.length === 1) {
          this.learningModule = response.modules[0];
          await Promise.all([
            this.loadIntroFromModule(),
            this.loadCompletionFromModule(),
            this.loadSurveyCardFromModule(),
            this.loadGroupsFromModule(),
          ]);
        }
      } catch (err) {
        console.error("Error loading learning module", err);
      } finally {
        this.loadingLearningModule = false;
      }
    },
    async loadCardFromChapter(chapterFilter, target) {
      const chapters = this.learningModule.LearningChapters || [];
      const chapter = chapters.find(chapterFilter);
      if (!chapter || !chapter.LearningCards?.length) return false;

      const card = chapter.LearningCards[0];
      target.learningChapterId = chapter.learningChapterId;
      target.learningCardId = card.learningCardId;
      target.displayName = card.displayName || "";
      target.content = await loadEmbeddedContent(card.content || "");
      target.type = card.type || "content";
      return true;
    },
    async loadIntroFromModule() {
      const found = await this.loadCardFromChapter(
        (ch) => ch.introduction === true,
        this.introductionCard
      );
      if (found) this.introductionEnabled = true;
    },
    async loadSurveyCardFromModule() {
      const chapters = this.learningModule.LearningChapters || [];
      const surveyChapter = chapters.find((ch) => !ch.introduction && !ch.conclusion);
      if (!surveyChapter || !surveyChapter.LearningCards?.length) return;

      const card = surveyChapter.LearningCards[0];
      this.surveyChapterId = surveyChapter.learningChapterId;
      this.surveyCardId = card.learningCardId;
      this.repeatable = card.repeatable || false;

      if (card?.LearningQuizQuestions?.length) {
        this.questions = card.LearningQuizQuestions.map((q) => ({
          ...q,
          LearningQuizAnswers: (q.LearningQuizAnswers || []).map((a) => ({ ...a })),
        }));
      }
    },
    async loadGroupsFromModule() {
      const moduleGroups = this.learningModule.LearningModuleGroups || [];
      if (!moduleGroups.length) return;

      const groupIds = moduleGroups.map((g) => g.groupId);
      const hasEveryone = groupIds.includes(0);

      const realGroupIds = groupIds.filter((id) => id !== 0);
      if (realGroupIds.length) {
        try {
          const filter = `groupId.isIn(${JSON.stringify(realGroupIds)}) && status =='Active'`;
          const response = await GroupService.getGroupsV2({
            filter,
            limit: realGroupIds.length,
            offset: 0,
            extract: "groupDisplayName,clientId,groupId,groupType",
          });
          this.selectedGroups = response.result.rows;
        } catch (err) {
          console.error("Error loading groups for module", err);
        }
      }

      if (hasEveryone) {
        this.selectedGroups.unshift({ groupId: 0, groupDisplayName: "Everyone" });
      }
    },
    async loadCompletionFromModule() {
      await this.loadCardFromChapter((ch) => ch.conclusion === true, this.completionCard);
    },
  },
  mounted() {
    this.loadLearningModule();
  },
  watch: {
    globalClientId: {
      immediate: true,
      handler(id) {
        this.learningModule.clientId = id;
      },
    },
    learningModule: {
      immediate: true,
      handler(newVal) {
        console.log("learningModule changed", newVal);
      },
    },
  },
};
</script>
